import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  collection,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";

import { db } from "../../firebase";

/* -------------------------------------------------------------------------- */
/*                                  Helpers                                   */
/* -------------------------------------------------------------------------- */

const clean = (value) =>
  String(value ?? "")
    .replace(/\s+/g, " ")
    .trim();

const lower = (value) =>
  clean(value).toLowerCase();

const normalizeRoom = (value) =>
  clean(value).toUpperCase();

const getDateObject = (value) => {
  if (!value) return null;

  if (typeof value?.toDate === "function") {
    return value.toDate();
  }

  if (value?.seconds) {
    return new Date(value.seconds * 1000);
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? null
    : date;
};

const getTimestamp = (value) =>
  getDateObject(value)?.getTime() || 0;

const formatDateTime = (value) => {
  const date = getDateObject(value);

  if (!date) {
    return clean(value) || "-";
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getTaskStatus = (task = {}) => {
  const status = clean(
    task.cleaningStatus ||
      task.status
  ).toLowerCase();

  if (
    status === "maintenance_hold" ||
    task.maintenanceRequired === true
  ) {
    return "maintenance_hold";
  }

  if (
    status === "completed" ||
    status === "vacant" ||
    task.roomReady === true
  ) {
    return "completed";
  }

  if (
    status === "inspection_pending"
  ) {
    return "inspection_pending";
  }

  if (
    status === "cleaning" ||
    status === "in_progress"
  ) {
    return "cleaning";
  }

  return "pending";
};

const getProgress = (task = {}) => {
  const savedPercentage = Number(
    task.checklistPercentage ??
      task.cleaningChecklistPercentage
  );

  if (
    Number.isFinite(savedPercentage) &&
    savedPercentage >= 0
  ) {
    return Math.min(
      100,
      Math.round(savedPercentage)
    );
  }

  const checklist =
    task.cleaningChecklist || {};

  const values =
    Object.values(checklist);

  if (!values.length) return 0;

  const completed =
    values.filter(Boolean).length;

  return Math.round(
    (completed / values.length) * 100
  );
};

const getSearchText = (task) =>
  [
    task.room,
    task.guestName,
    task.assignedTo,
    task.inspectedBy,
    task.cleaningStatus,
    task.status,
    task.housekeepingNote,
    task.missingItems,
    task.damagedItems,
    task.maintenanceDescription,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

/* -------------------------------------------------------------------------- */
/*                            Housekeeping Panel                              */
/* -------------------------------------------------------------------------- */

function HousekeepingPanel({
  hotelId,
  hotelName,

  onOpenRoom,
}) {
  const [tasks, setTasks] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [loadError, setLoadError] =
    useState("");

  const [search, setSearch] =
    useState("");

  const [statusFilter, setStatusFilter] =
    useState("active");

  const [staffFilter, setStaffFilter] =
    useState("all");

  const [sortBy, setSortBy] =
    useState("room");

  /* ------------------------------------------------------------------------ */
  /*                           Firestore Listener                              */
  /* ------------------------------------------------------------------------ */

  useEffect(() => {
    if (!hotelId) {
      setTasks([]);
      setLoading(false);
      setLoadError(
        "Hotel ID missing hai."
      );

      return undefined;
    }

    setLoading(true);
    setLoadError("");

    const tasksQuery = query(
      collection(
        db,
        "housekeepingTasks"
      ),

      where(
        "hotelId",
        "==",
        hotelId
      )
    );

    return onSnapshot(
      tasksQuery,

      (snapshot) => {
        const taskList =
          snapshot.docs
            .map((taskDocument) => ({
              id: taskDocument.id,
              ...taskDocument.data(),
            }))
            .sort((first, second) =>
              normalizeRoom(
                first.room
              ).localeCompare(
                normalizeRoom(
                  second.room
                ),
                undefined,
                {
                  numeric: true,
                }
              )
            );

        setTasks(taskList);
        setLoading(false);
        setLoadError("");
      },

      (error) => {
        console.error(
          "Housekeeping tasks load error:",
          error
        );

        setTasks([]);
        setLoading(false);

        setLoadError(
          error?.message ||
            "Housekeeping tasks load nahi hui."
        );
      }
    );
  }, [hotelId]);

  /* ------------------------------------------------------------------------ */
  /*                              Staff List                                   */
  /* ------------------------------------------------------------------------ */

  const staffNames = useMemo(
    () =>
      Array.from(
        new Set(
          tasks
            .map((task) =>
              clean(task.assignedTo)
            )
            .filter(Boolean)
        )
      ).sort((first, second) =>
        first.localeCompare(second)
      ),
    [tasks]
  );

  /* ------------------------------------------------------------------------ */
  /*                                Summary                                    */
  /* ------------------------------------------------------------------------ */

  const summary = useMemo(() => {
    const result = {
      total: tasks.length,
      pending: 0,
      cleaning: 0,
      inspectionPending: 0,
      maintenanceHold: 0,
      completed: 0,
      unassigned: 0,
      averageProgress: 0,
    };

    let totalProgress = 0;

    tasks.forEach((task) => {
      const status =
        getTaskStatus(task);

      totalProgress +=
        getProgress(task);

      if (status === "pending") {
        result.pending += 1;
      }

      if (status === "cleaning") {
        result.cleaning += 1;
      }

      if (
        status ===
        "inspection_pending"
      ) {
        result.inspectionPending += 1;
      }

      if (
        status ===
        "maintenance_hold"
      ) {
        result.maintenanceHold += 1;
      }

      if (status === "completed") {
        result.completed += 1;
      }

      if (!clean(task.assignedTo)) {
        result.unassigned += 1;
      }
    });

    result.averageProgress =
      tasks.length > 0
        ? Math.round(
            totalProgress /
              tasks.length
          )
        : 0;

    return result;
  }, [tasks]);

  /* ------------------------------------------------------------------------ */
  /*                          Filter and Sorting                               */
  /* ------------------------------------------------------------------------ */

  const filteredTasks = useMemo(() => {
    const searchTerm =
      lower(search);

    const taskList =
      tasks.filter((task) => {
        const status =
          getTaskStatus(task);

        const matchesSearch =
          !searchTerm ||
          getSearchText(task).includes(
            searchTerm
          );

        const matchesStatus =
          statusFilter === "all" ||
          (statusFilter === "active" &&
            status !== "completed") ||
          status === statusFilter;

        const matchesStaff =
          staffFilter === "all" ||
          (staffFilter ===
            "unassigned" &&
            !clean(task.assignedTo)) ||
          clean(task.assignedTo) ===
            staffFilter;

        return (
          matchesSearch &&
          matchesStatus &&
          matchesStaff
        );
      });

    return [...taskList].sort(
      (first, second) => {
        if (sortBy === "progress") {
          return (
            getProgress(second) -
            getProgress(first)
          );
        }

        if (sortBy === "oldest") {
          return (
            getTimestamp(
              first.checkedOutAt ||
                first.createdAt
            ) -
            getTimestamp(
              second.checkedOutAt ||
                second.createdAt
            )
          );
        }

        if (sortBy === "recent") {
          return (
            getTimestamp(
              second.checkedOutAt ||
                second.updatedAt
            ) -
            getTimestamp(
              first.checkedOutAt ||
                first.updatedAt
            )
          );
        }

        if (sortBy === "staff") {
          return clean(
            first.assignedTo
          ).localeCompare(
            clean(second.assignedTo)
          );
        }

        return normalizeRoom(
          first.room
        ).localeCompare(
          normalizeRoom(second.room),
          undefined,
          {
            numeric: true,
          }
        );
      }
    );
  }, [
    tasks,
    search,
    statusFilter,
    staffFilter,
    sortBy,
  ]);

  /* ------------------------------------------------------------------------ */
  /*                                  Render                                   */
  /* ------------------------------------------------------------------------ */

  return (
    <section style={sectionStyle}>
      <div style={headerStyle}>
        <div>
          <h3 style={titleStyle}>
            🧹 Housekeeping Panel
          </h3>

          <div style={subtitleStyle}>
            {hotelName || hotelId} ke
            cleaning, inspection aur
            room-readiness tasks
          </div>
        </div>

        <span style={activeBadgeStyle}>
          {summary.total -
            summary.completed}{" "}
          Active
        </span>
      </div>

      <div style={summaryGridStyle}>
        <SummaryCard
          label="Cleaning Pending"
          value={summary.pending}
          warning={
            summary.pending > 0
          }
        />

        <SummaryCard
          label="Cleaning"
          value={summary.cleaning}
        />

        <SummaryCard
          label="Inspection Pending"
          value={
            summary.inspectionPending
          }
          warning={
            summary.inspectionPending >
            0
          }
        />

        <SummaryCard
          label="Maintenance Hold"
          value={
            summary.maintenanceHold
          }
          danger={
            summary.maintenanceHold > 0
          }
        />

        <SummaryCard
          label="Ready Rooms"
          value={summary.completed}
        />

        <SummaryCard
          label="Unassigned"
          value={summary.unassigned}
          danger={
            summary.unassigned > 0
          }
        />

        <SummaryCard
          label="Average Progress"
          value={`${summary.averageProgress}%`}
        />
      </div>

      <div style={toolbarStyle}>
        <input
          value={search}
          onChange={(event) =>
            setSearch(
              event.target.value
            )
          }
          placeholder="Room, guest, staff, note ya issue search karo"
          style={inputStyle}
        />

        <select
          value={statusFilter}
          onChange={(event) =>
            setStatusFilter(
              event.target.value
            )
          }
          style={inputStyle}
        >
          <option value="active">
            Active Tasks
          </option>

          <option value="all">
            All Tasks
          </option>

          <option value="pending">
            Cleaning Pending
          </option>

          <option value="cleaning">
            Cleaning in Progress
          </option>

          <option value="inspection_pending">
            Inspection Pending
          </option>

          <option value="maintenance_hold">
            Maintenance Hold
          </option>

          <option value="completed">
            Room Ready
          </option>
        </select>

        <select
          value={staffFilter}
          onChange={(event) =>
            setStaffFilter(
              event.target.value
            )
          }
          style={inputStyle}
        >
          <option value="all">
            All Staff
          </option>

          <option value="unassigned">
            Unassigned
          </option>

          {staffNames.map((staff) => (
            <option
              key={staff}
              value={staff}
            >
              {staff}
            </option>
          ))}
        </select>

        <select
          value={sortBy}
          onChange={(event) =>
            setSortBy(
              event.target.value
            )
          }
          style={inputStyle}
        >
          <option value="room">
            Room Number
          </option>

          <option value="recent">
            Recent First
          </option>

          <option value="oldest">
            Oldest First
          </option>

          <option value="progress">
            Highest Progress
          </option>

          <option value="staff">
            Staff Name
          </option>
        </select>
      </div>

      {loadError && (
        <div style={errorBoxStyle}>
          {loadError}
        </div>
      )}

      {loading ? (
        <div style={emptyBoxStyle}>
          Housekeeping tasks loading...
        </div>
      ) : filteredTasks.length === 0 ? (
        <div style={emptyBoxStyle}>
          Matching housekeeping task
          nahi mili.
        </div>
      ) : (
        <div style={taskGridStyle}>
          {filteredTasks.map((task) => {
            const progress =
              getProgress(task);

            const status =
              getTaskStatus(task);

            return (
              <button
                key={task.id}
                type="button"
                onClick={() =>
                  onOpenRoom?.({
                    ...task,
                    room: normalizeRoom(
                      task.room
                    ),
                  })
                }
                style={{
                  ...taskCardStyle,

                  borderColor:
                    status ===
                    "maintenance_hold"
                      ? "#fecaca"
                      : status ===
                        "inspection_pending"
                      ? "#ddd6fe"
                      : "#e2e8f0",

                  background:
                    status ===
                    "maintenance_hold"
                      ? "#fff7f7"
                      : "#ffffff",
                }}
              >
                <div style={taskTopStyle}>
                  <div>
                    <div style={roomTitleStyle}>
                      Room{" "}
                      {normalizeRoom(
                        task.room
                      ) || "-"}
                    </div>

                    <div style={guestTextStyle}>
                      Previous Guest:{" "}
                      {task.guestName || "-"}
                    </div>
                  </div>

                  <StatusBadge
                    status={status}
                  />
                </div>

                <div style={progressAreaStyle}>
                  <div
                    style={progressHeaderStyle}
                  >
                    <span>
                      Checklist Progress
                    </span>

                    <strong>
                      {progress}%
                    </strong>
                  </div>

                  <div
                    style={progressTrackStyle}
                  >
                    <div
                      style={{
                        ...progressFillStyle,
                        width: `${progress}%`,
                      }}
                    />
                  </div>
                </div>

                <div style={detailGridStyle}>
                  <DetailItem
                    label="Assigned To"
                    value={
                      task.assignedTo ||
                      "Not Assigned"
                    }
                    danger={
                      !task.assignedTo
                    }
                  />

                  <DetailItem
                    label="Checkout"
                    value={formatDateTime(
                      task.checkedOutAt ||
                        task.actualCheckOutAt
                    )}
                  />

                  <DetailItem
                    label="Cleaning Started"
                    value={formatDateTime(
                      task.cleaningStartedAt
                    )}
                  />

                  <DetailItem
                    label="Inspection"
                    value={
                      task.inspectedBy ||
                      "Pending"
                    }
                  />
                </div>

                {task.maintenanceRequired && (
                  <div
                    style={
                      maintenanceWarningStyle
                    }
                  >
                    🔧 Maintenance Hold:{" "}
                    {task.maintenanceDescription ||
                      "Issue details unavailable"}
                  </div>
                )}

                {(task.missingItems ||
                  task.damagedItems) && (
                  <div style={inventoryWarningStyle}>
                    {task.missingItems
                      ? `Missing: ${task.missingItems}`
                      : ""}

                    {task.missingItems &&
                    task.damagedItems
                      ? " • "
                      : ""}

                    {task.damagedItems
                      ? `Damaged: ${task.damagedItems}`
                      : ""}
                  </div>
                )}

                <div style={openTextStyle}>
                  Open Cleaning Details →
                </div>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}

/* -------------------------------------------------------------------------- */
/*                             Small Components                               */
/* -------------------------------------------------------------------------- */

function SummaryCard({
  label,
  value,
  danger = false,
  warning = false,
}) {
  return (
    <div style={summaryCardStyle}>
      <span style={summaryLabelStyle}>
        {label}
      </span>

      <strong
        style={{
          ...summaryValueStyle,

          color: danger
            ? "#dc2626"
            : warning
            ? "#c2410c"
            : "#0f172a",
        }}
      >
        {value}
      </strong>
    </div>
  );
}

function DetailItem({
  label,
  value,
  danger = false,
}) {
  return (
    <div>
      <span style={detailLabelStyle}>
        {label}
      </span>

      <strong
        style={{
          ...detailValueStyle,

          color: danger
            ? "#dc2626"
            : "#334155",
        }}
      >
        {value}
      </strong>
    </div>
  );
}

function StatusBadge({
  status,
}) {
  const config = {
    pending: {
      label: "Cleaning Pending",
      background: "#fef3c7",
      color: "#92400e",
    },

    cleaning: {
      label: "Cleaning",
      background: "#dbeafe",
      color: "#1d4ed8",
    },

    inspection_pending: {
      label: "Inspection Pending",
      background: "#f3e8ff",
      color: "#7c3aed",
    },

    maintenance_hold: {
      label: "Maintenance Hold",
      background: "#fee2e2",
      color: "#b91c1c",
    },

    completed: {
      label: "Room Ready",
      background: "#dcfce7",
      color: "#15803d",
    },
  };

  const selected =
    config[status] ||
    config.pending;

  return (
    <span
      style={{
        ...statusBadgeStyle,
        background:
          selected.background,
        color: selected.color,
      }}
    >
      {selected.label}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/*                                   Styles                                   */
/* -------------------------------------------------------------------------- */

const sectionStyle = {
  marginTop: 34,
  padding: 18,

  border:
    "1px solid #e2e8f0",

  borderRadius: 18,

  background: "#ffffff",
};

const headerStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",

  gap: 12,
  flexWrap: "wrap",
};

const titleStyle = {
  margin: 0,
  color: "#0f172a",
};

const subtitleStyle = {
  marginTop: 5,

  color: "#64748b",

  fontSize: 12,
};

const activeBadgeStyle = {
  padding: "8px 12px",

  borderRadius: 999,

  background: "#dbeafe",
  color: "#1d4ed8",

  fontWeight: 900,
};

const summaryGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(125px,1fr))",

  gap: 9,

  marginTop: 15,
};

const summaryCardStyle = {
  padding: 11,

  border:
    "1px solid #e2e8f0",

  borderRadius: 11,

  background: "#f8fafc",
};

const summaryLabelStyle = {
  display: "block",

  color: "#64748b",

  fontSize: 9,
  fontWeight: 800,

  textTransform: "uppercase",
};

const summaryValueStyle = {
  display: "block",

  marginTop: 4,

  fontSize: 18,
  fontWeight: 900,
};

const toolbarStyle = {
  display: "grid",

  gridTemplateColumns:
    "minmax(230px,1fr) repeat(3,minmax(150px,200px))",

  gap: 9,

  marginTop: 15,
};

const inputStyle = {
  width: "100%",
  minHeight: 42,

  padding: 10,

  border:
    "1px solid #cbd5e1",

  borderRadius: 9,

  background: "#ffffff",

  boxSizing: "border-box",

  outline: "none",
};

const errorBoxStyle = {
  marginTop: 13,
  padding: 11,

  border:
    "1px solid #fecaca",

  borderRadius: 10,

  background: "#fef2f2",
  color: "#b91c1c",

  fontSize: 12,
  fontWeight: 700,
};

const emptyBoxStyle = {
  marginTop: 14,
  padding: 18,

  border:
    "1px dashed #cbd5e1",

  borderRadius: 11,

  color: "#64748b",

  textAlign: "center",
};

const taskGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(auto-fit,minmax(290px,1fr))",

  gap: 11,

  marginTop: 15,
};

const taskCardStyle = {
  padding: 14,

  border:
    "1px solid #e2e8f0",

  borderRadius: 14,

  textAlign: "left",

  cursor: "pointer",

  boxShadow:
    "0 4px 14px rgba(15,23,42,.04)",
};

const taskTopStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",

  gap: 10,
};

const roomTitleStyle = {
  color: "#0f172a",

  fontSize: 18,
  fontWeight: 900,
};

const guestTextStyle = {
  marginTop: 4,

  color: "#64748b",

  fontSize: 10,
};

const statusBadgeStyle = {
  display: "inline-block",

  padding: "5px 8px",

  borderRadius: 999,

  fontSize: 9,
  fontWeight: 900,

  whiteSpace: "nowrap",
};

const progressAreaStyle = {
  marginTop: 13,
};

const progressHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",

  gap: 10,

  marginBottom: 6,

  color: "#475569",

  fontSize: 10,
};

const progressTrackStyle = {
  height: 9,

  overflow: "hidden",

  borderRadius: 999,

  background: "#e2e8f0",
};

const progressFillStyle = {
  height: "100%",

  borderRadius: 999,

  background: "#16a34a",
};

const detailGridStyle = {
  display: "grid",

  gridTemplateColumns:
    "repeat(2,minmax(0,1fr))",

  gap: 9,

  marginTop: 13,
  paddingTop: 11,

  borderTop:
    "1px solid #e2e8f0",
};

const detailLabelStyle = {
  display: "block",

  color: "#64748b",

  fontSize: 8,
  fontWeight: 800,

  textTransform: "uppercase",
};

const detailValueStyle = {
  display: "block",

  marginTop: 3,

  fontSize: 10,
  fontWeight: 800,

  overflowWrap: "anywhere",
};

const maintenanceWarningStyle = {
  marginTop: 11,
  padding: 8,

  borderRadius: 8,

  background: "#fef2f2",
  color: "#b91c1c",

  fontSize: 10,
  fontWeight: 700,
};

const inventoryWarningStyle = {
  marginTop: 8,
  padding: 8,

  borderRadius: 8,

  background: "#fff7ed",
  color: "#9a3412",

  fontSize: 10,
};

const openTextStyle = {
  marginTop: 11,

  color: "#2563eb",

  fontSize: 10,
  fontWeight: 900,

  textAlign: "right",
};

export default HousekeepingPanel;